import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { CustomFieldItem, CustomFieldType } from '@recruitee/custom-fields-types';
import { FeatureSpecSelectors } from '@recruitee/feature-spec-data';
import { FeatureCode } from '@recruitee/feature-spec-types';
import { I18nService } from '@recruitee/i18n';
import type { IconName, SelectOption } from '@recruitee/user-interface';

@Injectable({ providedIn: 'root' })
export class CustomFieldsHelperService {
  private readonly store = inject(Store);
  private readonly i18nService = inject(I18nService);

  private readonly icons: Record<CustomFieldType, IconName> = {
    [CustomFieldType.SingleLine]: 'short-text',
    [CustomFieldType.SingleChoice]: 'done',
    [CustomFieldType.MultiChoice]: 'done-all',
    [CustomFieldType.Boolean]: 'radio-button-checked',
    [CustomFieldType.Number]: 'numeric',
    [CustomFieldType.Money]: 'cash',
    [CustomFieldType.Date]: 'calendar-today',
    [CustomFieldType.SingleUser]: 'person',
    [CustomFieldType.MultiUser]: 'people',
  };

  private readonly translations: Record<CustomFieldType, string> = {
    [CustomFieldType.SingleLine]: this.i18nService.translate(
      'app.custom_fields.types.single_line_v2',
    ),
    [CustomFieldType.SingleChoice]: this.i18nService.translate(
      'app.custom_fields.types.single_choice',
    ),
    [CustomFieldType.MultiChoice]: this.i18nService.translate(
      'app.custom_fields.types.multiple_choice',
    ),
    [CustomFieldType.Boolean]: this.i18nService.translate('app.custom_fields.types.boolean'),
    [CustomFieldType.Number]: this.i18nService.translate('app.custom_fields.types.number'),
    [CustomFieldType.Money]: this.i18nService.translate('app.custom_fields.types.money'),
    [CustomFieldType.Date]: this.i18nService.translate('app.custom_fields.types.date'),
    [CustomFieldType.SingleUser]: this.i18nService.translate('app.custom_fields.types.single_user'),
    [CustomFieldType.MultiUser]: this.i18nService.translate(
      'app.custom_fields.types.multiple_users',
    ),
  };

  private readonly customFieldsFeatureSpec = this.store.selectSignal(
    FeatureSpecSelectors.forCode(FeatureCode.CustomFields),
  );

  public mapFieldTypeToIcon(fieldType: CustomFieldType): IconName {
    return this.icons[fieldType];
  }

  public getFieldTypeOptions(): SelectOption[] {
    return [
      {
        value: CustomFieldType.SingleLine,
        label: this.translations[CustomFieldType.SingleLine],
        icon: this.mapFieldTypeToIcon(CustomFieldType.SingleLine),
      },
      {
        value: CustomFieldType.SingleChoice,
        label: this.translations[CustomFieldType.SingleChoice],
        icon: this.mapFieldTypeToIcon(CustomFieldType.SingleChoice),
      },
      {
        value: CustomFieldType.MultiChoice,
        label: this.translations[CustomFieldType.MultiChoice],
        icon: this.mapFieldTypeToIcon(CustomFieldType.MultiChoice),
      },
      {
        value: CustomFieldType.Boolean,
        label: this.translations[CustomFieldType.Boolean],
        icon: this.mapFieldTypeToIcon(CustomFieldType.Boolean),
      },
      {
        value: CustomFieldType.Number,
        label: this.translations[CustomFieldType.Number],
        icon: this.mapFieldTypeToIcon(CustomFieldType.Number),
      },
      {
        value: CustomFieldType.Money,
        label: this.translations[CustomFieldType.Money],
        icon: this.mapFieldTypeToIcon(CustomFieldType.Money),
      },
      {
        value: CustomFieldType.Date,
        label: this.translations[CustomFieldType.Date],
        icon: this.mapFieldTypeToIcon(CustomFieldType.Date),
      },
      {
        value: CustomFieldType.SingleUser,
        label: this.translations[CustomFieldType.SingleUser],
        icon: this.mapFieldTypeToIcon(CustomFieldType.SingleUser),
      },
      {
        value: CustomFieldType.MultiUser,
        label: this.translations[CustomFieldType.MultiUser],
        icon: this.mapFieldTypeToIcon(CustomFieldType.MultiUser),
      },
    ];
  }

  public getAssignedItemsCountLabel(
    customField: Pick<CustomFieldItem, 'offersCount' | 'offerTemplatesCount' | 'requisitionsCount'>,
    useAndSeparator = true,
  ): string {
    const jobs = customField.offersCount
      ? `${customField.offersCount} ${this.i18nService.translate('app.plurals.job', { count: customField.offersCount })}`
      : '';
    const jobTemplates = customField.offerTemplatesCount
      ? `${customField.offerTemplatesCount} ${this.i18nService.translate('app.plurals.job_template', { count: customField.offerTemplatesCount })}`
      : '';
    const requisitions = customField.requisitionsCount
      ? `${customField.requisitionsCount} ${this.i18nService.translate('app.plurals.requisition', { count: customField.requisitionsCount })}`
      : '';
    return [jobs, jobTemplates, requisitions].filter(Boolean).reduce((acc, curr, index, arr) => {
      const isFirst = index === 0;
      const isLast = index === arr.length - 1;

      if (isLast && !isFirst) {
        return (
          acc +
          `${useAndSeparator ? ' ' + this.i18nService.translate('app.common.and') : ','} ${curr}`
        );
      }

      return acc + `${isFirst ? '' : ', '}${curr}`;
    }, '');
  }

  public getIsCustomFieldsLimitReached(customFieldsAmount: number) {
    const customFieldsFeatureSpec = this.customFieldsFeatureSpec();

    if (!customFieldsFeatureSpec) return false;

    const { enabled, configuration } = customFieldsFeatureSpec;
    const limit = configuration.limit ?? Number.POSITIVE_INFINITY;
    return enabled ? customFieldsAmount >= limit : false;
  }

  public getIsCustomFieldsLimitExceeded(customFieldsAmount: number) {
    const customFieldsFeatureSpec = this.customFieldsFeatureSpec();

    if (!customFieldsFeatureSpec) return false;

    const { enabled, configuration } = customFieldsFeatureSpec;
    const limit = configuration.limit ?? Number.POSITIVE_INFINITY;
    return enabled ? customFieldsAmount > limit : false;
  }

  public getCustomFieldsLimitExceededCount(customFieldsAmount: number) {
    const customFieldsFeatureSpec = this.customFieldsFeatureSpec();

    if (!customFieldsFeatureSpec) return false;

    const { enabled, configuration } = customFieldsFeatureSpec;
    const limit = configuration.limit ?? Number.POSITIVE_INFINITY;
    return enabled ? customFieldsAmount - limit : 0;
  }
}
